// src/services/newsFrontend.js
// Frontend-only: attempts direct RSS fetch, falls back to AllOrigins proxy, parses XML to JSON.

async function fetchText(url) {
  // try direct fetch first
  try {
    const r = await fetch(url, { cache: "no-store" });
    if (!r.ok) throw new Error(`Status ${r.status}`);
    return await r.text();
  } catch (err) {
    // direct failed (likely CORS). fall through to proxy
  }

  // fallback: use AllOrigins proxy (no API key)
  // NOTE: service reliability not guaranteed for production
  const proxy = `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`;
  const r2 = await fetch(proxy, { cache: "no-store" });
  if (!r2.ok) throw new Error(`Proxy failed ${r2.status}`);
  return await r2.text();
}

function parseRssXml(xmlText) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xmlText, "application/xml");
  const items = Array.from(doc.querySelectorAll("item")).map((it) => {
    const titleEl = it.querySelector("title");
    const linkEl = it.querySelector("link");
    const dateEl = it.querySelector("pubDate") || it.querySelector("dc\\:date");
    const descriptionEl = it.querySelector("description");
    return {
      title: titleEl ? titleEl.textContent.trim() : "",
      link: linkEl ? (linkEl.textContent || linkEl.getAttribute("href") || "").trim() : "",
      pubDate: dateEl ? dateEl.textContent.trim() : null,
      description: descriptionEl ? descriptionEl.textContent.trim() : null,
    };
  });
  // fallback to <entry> (Atom)
  if (items.length === 0) {
    const entries = Array.from(doc.querySelectorAll("entry")).map((it) => ({
      title: (it.querySelector("title")?.textContent || "").trim(),
      link: (it.querySelector("link")?.getAttribute("href") || it.querySelector("link")?.textContent || "").trim(),
      pubDate: (it.querySelector("updated") || it.querySelector("published"))?.textContent?.trim() || null,
      description: (it.querySelector("summary") || it.querySelector("content"))?.textContent?.trim() || null,
    }));
    return entries;
  }
  return items;
}

/**
 * Fetch and parse multiple RSS feeds, merge and sort by date, return up to `limit` items.
 * @param {string[]} feedUrls
 * @param {number} limit
 */
export async function fetchCryptoNewsFrontend(feedUrls = [
  "https://www.coindesk.com/arc/outboundfeeds/rss/",
  "https://cointelegraph.com/rss",
  "https://www.newsbtc.com/feed/"
], limit = 8) {
  const all = [];
  for (const url of feedUrls) {
    try {
      const text = await fetchText(url);
      const parsed = parseRssXml(text);
      const normalized = parsed.map(p => ({
        title: p.title,
        link: p.link,
        pubDate: p.pubDate ? new Date(p.pubDate).toISOString() : null,
        description: p.description,
        source: (new URL(url)).hostname.replace('www.', '')
      }));
      all.push(...normalized);
    } catch (e) {
      // ignore this feed failing, continue others
      console.warn("Feed fetch failed", url, e);
    }
  }
  // sort by date desc (items without date go last)
  all.sort((a,b) => {
    const da = a.pubDate ? new Date(a.pubDate).getTime() : 0;
    const db = b.pubDate ? new Date(b.pubDate).getTime() : 0;
    return db - da;
  });
  return all.slice(0, limit);
}
